import React, { useState } from "react";
import HtmlArtifactFrame from "./HtmlArtifactFrame";

/**
 * Renders an issued certificate's artwork, whichever format it was issued in.
 *
 * SVG artwork is an image and renders in an `<img>`, as it always has. HTML
 * artwork **cannot**: a browser will not render an HTML document inside an
 * `<img>`, so it needs a frame. The backend tells us which we have via
 * `artifact_type` on every artwork-bearing response; anything unrecognised
 * (including a missing value, which is every pre-HTML credential) is treated
 * as SVG, so existing behaviour is unchanged.
 *
 * The frame is sandboxed with an empty `sandbox` attribute, which denies
 * scripts, forms and navigation. Published templates are sanitised server-side
 * and rendered with JavaScript disabled, so nothing legitimate needs them.
 */
export function isHtmlArtifact(artifactType) {
  return String(artifactType || "").toLowerCase() === "html";
}

export default function CertificateArtwork({
  url,
  artifactType,
  alt,
  title,
  className = "",
  onLoad,
  onError,
  maxHeightVh,
}) {
  const [loading, setLoading] = useState(true);

  const finish = (handler) => () => {
    setLoading(false);
    handler?.();
  };

  if (!url) return null;

  if (isHtmlArtifact(artifactType)) {
    return (
      <HtmlArtifactFrame
        url={url}
        title={title || alt || "Certificate"}
        loading="lazy"
        maxHeightVh={maxHeightVh}
        onLoad={finish(onLoad)}
        onError={finish(onError)}
        className={className}
      />
    );
  }

  return (
    <img
      src={url}
      alt={alt || "Certificate"}
      onLoad={finish(onLoad)}
      onError={finish(onError)}
      className={className}
      data-loading={loading ? "true" : "false"}
    />
  );
}
